import { MDM_COMMANDS_CATALOG } from '@staff-app/shared-types';

/**
 * Readable label for an open-api `deviceActivityFeed` row. The feed carries an i18n key, never
 * English — `mdm.activity.command.<wire command>`, `mdm.activity.vendorOp.<operation>`,
 * `mdm.activity.audit.<event>` — and scio3 renders it through `t(operationKey, params)`. staff-app has
 * no i18n (and scio3's en.json holds no `mdm.activity.*` strings either, so `t` falls back to the key
 * there too), so the label is derived here: a small map for the wire values we know, else the last
 * key segment humanized ("installOptiSignsVersion" → "Install opti signs version").
 */

// Wire command → label. Seeded from the shared command catalog (its `wireCommand` values), then the
// raw execution-rail names open-api's mdm.inputs.ts actually stores on command-execution rows.
const COMMAND_LABELS: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const cmd of MDM_COMMANDS_CATALOG) {
    const action = cmd.action as { wireCommand?: string };
    if (action.wireCommand) map[action.wireCommand] = cmd.i18nKey;
    map[cmd.id] = cmd.i18nKey;
  }
  return {
    ...map,
    REBOOT: 'Reboot',
    rebootDevice: 'Reboot',
    reboot: 'Reboot',
    refreshApp: 'Refresh & Relaunch',
    captureScreenShot: 'Take screenshot',
    CAPTURE_SCREENSHOT: 'Take screenshot',
    getDeviceInfo: 'Get device info',
    getUsedStorage: 'Storage Details',
    // Stored values of the dialog-driven commands (the catalog carries no wireCommand for those).
    hdmiCEC: 'Send HDMI CEC',
    rs232: 'Send RS232',
    installOptiSignsVersion: 'Install OptiSigns Versions',
    disableAutoUpdate: 'Disable Auto Update',
    enableAutoUpdate: 'Enable Auto Update',
    installAPk: 'Install APK',
    executeCommands: 'Execute Remote Commands',
    forceOTA: 'OTA update check',
    identifyScreen: 'Identify Screen',
    screenIdentity: 'Identify Screen',
    unknown: 'Unknown command',
  };
})();

const VENDOR_OP_LABELS: Record<string, string> = {
  pushSignage: 'Push signage',
  removeSignage: 'Remove signage',
  syncDevice: 'Sync device',
  revokeTokens: 'Revoke tokens',
  listDevices: 'List devices',
};

const AUDIT_LABELS: Record<string, string> = {
  renamed: 'Renamed',
  paired: 'Paired',
  fleetMoved: 'Moved to fleet',
  tagged: 'Tagged',
};

/** "installOptiSignsVersion" / "MDM_REBOOT" / "push-signage" → "Install opti signs version" etc. */
export function humanizeKey(segment: string): string {
  const spaced = segment
    .replace(/^MDM_/, '')
    .replace(/[_\-.]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : segment;
}

/** Readable label for a raw command-execution `command` (wire value or catalog id). */
export function commandLabel(command: string | null | undefined): string {
  if (!command) return 'Unknown command';
  return COMMAND_LABELS[command] ?? humanizeKey(command);
}

export function activityLabel(operationKey: string, params?: Record<string, unknown> | null): string {
  const key = operationKey ?? '';
  const match = /^mdm\.activity\.(command|vendorOp|audit)\.(.+)$/.exec(key);
  if (!match) return key ? humanizeKey(key.split('.').pop() ?? key) : 'Activity';
  const [, kind, name] = match;
  if (kind === 'command') {
    return COMMAND_LABELS[name] ?? humanizeKey(name);
  }
  if (kind === 'vendorOp') {
    const base = VENDOR_OP_LABELS[name] ?? humanizeKey(name);
    const portal = typeof params?.platformPortal === 'string' ? params.platformPortal : null;
    return portal ? `${base} (${portal})` : base;
  }
  return AUDIT_LABELS[name] ?? humanizeKey(name);
}
